import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Grid, TextField, Typography, styled } from '@mui/material';
import { Box } from '@mui/system';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';

import Services from '../../../shared/services';
import ConfirmationModal from '../../../shared/components/ui/modal/confirmation-modal';
import InformationForm from '../../../shared/components/ui/information-form';
import {
  ISupervisorApprovedPayload,
  ISupervisorResponse,
} from '../../../shared/interfaces/supervisor.interface';

const styles = {
  wrapper: {
    display: 'flex',
    justifyContent: 'center',
    height: '100vh',
    minWidth: '100%',
    padding: '2rem',
  },

  container: {
    width: '80%',
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },

  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    marginY: '2rem',
  },

  buttonStyle: {
    paddingX: '2rem',
    mb: 1,
    mr: 1,
    borderRadius: '2rem',
  },
};

const Header = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '1.75rem',
  marginBottom: '2rem',
}));

const SupervisorApproval = () => {
  const navigate = useNavigate();
  const { slug } = useParams<string>();

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [data, setData] = useState<ISupervisorResponse>();
  const [mode, setMode] = useState<string>('');

  const [formValue, setFormValue] = useState<ISupervisorApprovedPayload>({
    mode: '',
    /* eslint-disable camelcase */
    last_working_day: '',
    remarks: '',
  });

  const fetchData = async () => {
    if (slug) {
      const { data } = await Services.api.supervisor.fetch(slug);

      setData((prev: any) => ({ ...prev, data }));
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (data) fetchData();
  }, [data?.data.application.status]);

  const onModalAction = (e: string) => {
    if (e === 'confirm') {
      setFormValue((prev: ISupervisorApprovedPayload) => {
        return {
          ...prev,
          mode: mode,
          last_working_day: formValue.last_working_day
            ? formValue.last_working_day
            : data?.data.application.last_working_day,
        };
      });
    } else {
      setMode('');
      setModalOpen(false);
    }
  };

  useEffect(() => {
    if (formValue.mode) onDecideMode();
  }, [formValue.mode]);

  const onDecideMode = async () => {
    if (formValue.mode && formValue.last_working_day !== null && slug) {
      const { success, errors } = await Services.api.supervisor.decide(formValue, slug);
      const { mode } = formValue;

      if (success) {
        fetchData();
        setModalOpen(false);
        if (mode === 'ongoing') {
          navigate(`/supervisor/${slug}/success/approved`);
          Services.toast('success', 'Application approved successfully.');
        }

        if (mode === 'acknowledged') {
          navigate(`/supervisor/${slug}/success/acknowledged`);
          Services.toast('success', 'Application acknowledged successfully.');
        }

        if (mode === 'rejected') {
          navigate(`/supervisor/${slug}/success/rejected`);
          Services.toast('success', 'Application rejected successfully.');
        }
      } else {
        const [{ message }] = errors;

        setModalOpen(false);
        setFormValue((prev) => ({ ...prev, mode: '' }));
        Services.toast('error', message);
      }
    }
  };

  const onClickHandler = (action: string) => {
    setMode(action);
    setModalOpen(true);
  };

  const onChangeFormHandler = (e: string, identifier: string) => {
    setFormValue((prev: ISupervisorApprovedPayload) => ({ ...prev, [identifier]: e }));
  };

  const showLastWorkingDay = (status: any) => {
    if (
      status === 'created' ||
      status === 'approved' ||
      status === 'rejected' ||
      status === 'resolved'
    ) {
      return true;
    }

    return false;
  };

  return (
    <Box sx={styles.wrapper}>
      {data && (
        <>
          <Box sx={styles.container}>
            <InformationForm
              title='Online HR Clearance Approval Form'
              info={data?.data.application}
            />

            <Box sx={{ width: '100%', marginY: '2rem' }}>
              <Grid container spacing={1}>
                {showLastWorkingDay(data.data.application.status) && (
                  <>
                    <Grid item sx={{ marginTop: '2rem' }} xs={12} md={4}>
                      <DatePicker
                        label='Last Working Day'
                        inputFormat='MM/DD/YYYY'
                        value={
                          formValue.last_working_day
                            ? formValue.last_working_day
                            : data?.data.application.last_working_day
                        }
                        onChange={(e) => {
                          const date = moment(e).format('YYYY-MM-DD');
                          onChangeFormHandler(date, 'last_working_day');
                        }}
                        renderInput={(params: any) => <TextField fullWidth {...params} />}
                      />
                    </Grid>

                    <Grid item sx={{ marginTop: '2rem' }} xs={12}>
                      <TextField
                        label='Remarks'
                        multiline
                        rows={4}
                        value={formValue.remarks}
                        onChange={(e: any) => onChangeFormHandler(e.target.value, 'remarks')}
                        fullWidth
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              {(data?.data.application.status === 'approved' ||
                (data?.data.application.status === 'resolved' &&
                  data?.data.application.step === '1')) && (
                <Box sx={styles.buttonContainer}>
                  <Button
                    type='button'
                    variant='contained'
                    color='primary'
                    onClick={() => onClickHandler('acknowledged')}
                    size='large'
                    sx={styles.buttonStyle}
                    disableElevation
                  >
                    Acknowledged
                  </Button>

                  <Button
                    type='button'
                    variant='contained'
                    color='inherit'
                    onClick={() => onClickHandler('rejected')}
                    size='large'
                    sx={styles.buttonStyle}
                    disableElevation
                  >
                    Reject
                  </Button>
                </Box>
              )}

              {(data?.data.application.status === 'created' ||
                (data?.data.application.status === 'resolved' &&
                  data?.data.application.step === '0')) && (
                <Box sx={styles.buttonContainer}>
                  <Button
                    type='button'
                    variant='contained'
                    color='primary'
                    onClick={() => onClickHandler('ongoing')}
                    size='large'
                    sx={styles.buttonStyle}
                    disableElevation
                  >
                    Approved
                  </Button>

                  <Button
                    type='button'
                    variant='contained'
                    color='inherit'
                    onClick={() => onClickHandler('rejected')}
                    size='large'
                    sx={styles.buttonStyle}
                    disableElevation
                  >
                    Reject
                  </Button>
                </Box>
              )}

              {data?.data.application.status === 'rejected' && (
                <Box sx={styles.buttonContainer}>
                  <Button
                    type='button'
                    variant='contained'
                    color='primary'
                    onClick={() => onClickHandler('resolved')}
                    size='large'
                    sx={styles.buttonStyle}
                    disableElevation
                  >
                    Resolved
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          <ConfirmationModal
            isModalOpen={modalOpen}
            title={'Confirmation'}
            message={`Do you want to ${
              mode === 'ongoing' ? 'approve' : mode === 'acknowledged' ? 'acknowledge' : 'reject'
            } this application?`}
            onClickModal={(e: string) => onModalAction(e)}
          />
        </>
      )}
    </Box>
  );
};

export default SupervisorApproval;
